import { User } from '../../domain/entities/User';
import { AuthRepository, AuthUser } from '../../domain/repositories/AuthRepository';
import { UserRepository } from '../../domain/repositories/UserRepository';
import { AuthenticationError } from '../../domain/errors';
import { AuthenticationPort } from '../../domain/ports/AuthenticationPort';

export class AuthenticationService implements AuthenticationPort {
  constructor(
    private authRepository: AuthRepository,
    private userRepository: UserRepository
  ) {}

  async signIn(email: string, password: string): Promise<User> {
    try {
      const authResult = await this.authRepository.signInWithCredentials(email, password);
      return await this.userRepository.getUser(authResult.uid);
    } catch (error: any) {
      if (error instanceof AuthenticationError) {
        throw error;
      }
      throw new AuthenticationError("SIGN_IN_FAILED");
    }
  }

  async signOut(): Promise<void> {
    try {
      await this.authRepository.signOut();
    } catch (error: any) {
      if (error instanceof AuthenticationError) {
        throw error;
      }
      throw new AuthenticationError("SIGN_OUT_FAILED");
    }
  }

  async getCurrentUser(): Promise<User | null> {
    const authUser = this.authRepository.getCurrentAuthUser();
    if (!authUser) {
      return null;
    }

    try {
      return await this.userRepository.getUser(authUser.uid);
    } catch (error) {
      return null;
    }
  }

  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    return this.authRepository.onAuthStateChanged(async (authUser: AuthUser | null) => {
      if (authUser) {
        try {
          const user = await this.userRepository.getUser(authUser.uid);
          callback(user);
        } catch (error) {
          callback(null);
        }
      } else {
        callback(null);
      }
    });
  }
}